// reconnectWebSocket.js - Utility for WebSocket reconnection functionality
import logger from './Logger';
import {
  normalizeWarningPayload,
  deriveWarningDuration,
  handleWarningToastAndCountdown,
} from './wsErrorCodes';

const COMPONENT = 'ReconnectWebSocket';
// Cookie helpers
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
}

function setCookie(name, value, expirationDate = null) {
  let cookieString = `${name}=${value}; path=/`;
  if (expirationDate) {
    cookieString += `; expires=${expirationDate}`;
  }
  document.cookie = cookieString;
}

// Get stored transcription model type
function getStoredModel() {
  return getCookie('transcription_model') || 'assemblyai'; // Default to assemblyai
}

// Get stored token
function getStoredToken() {
  const token = getCookie('transcription_token');
  const expiresAt = getCookie('token_expires_at');
  
  if (!token || !expiresAt) {
    logger.debug('Token or expiration not found in cookies', {
      component: COMPONENT,
      action: 'GET_STORED_TOKEN',
      context: { hasToken: !!token, hasExpiresAt: !!expiresAt }
    });
    return null;
  }
  
  try {
    let expiresAtUTC = expiresAt;
    if (!expiresAt.endsWith('Z') && !expiresAt.includes('+') && !expiresAt.includes('-', 10)) {
      expiresAtUTC = expiresAt + 'Z';
    }
    
    const currentUTCTimestamp = Date.now();
    const expirationDate = new Date(expiresAtUTC);
    const expirationUTCTimestamp = expirationDate.getTime();
    
    if (isNaN(expirationUTCTimestamp)) {
      logger.error('Invalid token expiration date in reconnection', {
        component: COMPONENT,
        action: 'VALIDATE_TOKEN_EXPIRY',
        context: { expiresAt, expiresAtUTC }
      });
      console.error('❌ Invalid expiration date:', expiresAt);
      return null;
    }
    
    const isExpired = currentUTCTimestamp >= expirationUTCTimestamp;
    if (isExpired) {
      logger.warn('Token expired during reconnection attempt', {
        component: COMPONENT,
        action: 'VALIDATE_TOKEN_EXPIRY',
        context: {
          currentUTCTimestamp,
          expirationUTCTimestamp,
          timeDiffMs: currentUTCTimestamp - expirationUTCTimestamp
        }
      });
    }
    return isExpired ? null : token;
  } catch (error) {
    logger.error('Failed to parse token expiration during reconnection', {
      component: COMPONENT,
      action: 'VALIDATE_TOKEN_EXPIRY',
      error,
      context: { rawValue: expiresAt }
    });
    console.error('❌ Error parsing token expiration date:', error, 'Raw value:', expiresAt);
    return null;
  }
}

// Store configuration response
function storeConfigurationResponse(configResponse) {
  const modelDetails = configResponse.model_details || {};
  const { token, issued_at, expires_at, status, model } = modelDetails;

  logger.info('Storing configuration response during reconnection', {
    component: COMPONENT,
    action: 'STORE_CONFIG_RESPONSE',
    context: { status, model, hasToken: !!token }
  });
  
  setCookie('token_issued_at', issued_at);
  setCookie('token_expires_at', expires_at);
  setCookie('token_status', status);

  if (model) {
    setCookie('transcription_model', model);
  }
  
  if (status === 'new' || status === 'existing') {
    setCookie('transcription_token', token);
  } else {
    logger.error('Unknown token status received during reconnection', {
      component: COMPONENT,
      action: 'STORE_CONFIG_RESPONSE',
      context: { status, model }
    });
    console.warn('⚠️ Unknown token status:', status);
  }
}

// Utility to format timestamp
function getFormattedTimestamp(dateObj) {
  return dateObj.toISOString().slice(0, 19);
}


/**
 * Reconnects the WebSocket connection
 * @param {Object} params - Parameters object
 * @param {Function} params.getAuthenticatedUserId - Function to get authenticated user ID
 * @param {string} params.conversationId - Current conversation ID
 * @param {string} params.conversationIdFromUrl - Conversation ID from URL
 * @param {string} params.sessionId - Current session ID
 * @param {Object} params.socketRef - WebSocket reference
 * @param {Object} params.webSocketConnectedRef - WebSocket connection status reference
 * @param {Object} params.wsConnectLatchRef - WebSocket connection latch reference
 * @param {Object} params.backendReconnectionAttemptsRef - Backend reconnection attempts reference
 * @param {Object} params.isBackendReconnectingRef - Backend reconnecting status reference
 * @param {Object} params.backendReconnectionTimeoutRef - Backend reconnection timeout reference
 * @param {Object} params.persistentDataRequestedRef - Persistent data requested status reference
 * @param {string} params.websocketBaseUrl - WebSocket base URL
 * @param {Function} params.requestToken - Function to request token
 * @param {Function} params.handlePersistentData - Function to handle persistent data
 * @param {Function} params.startTranscriptionStreamWithToken - Function to start transcription stream
 * @param {Function} params.startTokenExpiryTimer - Function to start token expiry timer
 * @param {Function} params.getSessionIdForConversation - Function to get session ID for conversation
 * @param {Function} params.setSessionId - Function to set session ID
 * @param {Function} params.setDateString - Function to set date string
 * @param {Function} params.setTimeString - Function to set time string
 * @param {Object} params.assemblySocketRef - Assembly socket reference
 * @param {boolean} params.isLive - Live recording status
 * @param {Object} params.userInitiatedStopRef - User initiated stop reference
 * @param {Function} params.showPlanWarningToast - Function to show plan/time-limit warning toast
 * @param {Function} params.schedulePlanCountdownFromPayload - Function to schedule footer countdown
 */
export const reconnectWebSocket = ({
  getAuthenticatedUserId,
  conversationId,
  conversationIdFromUrl,
  sessionId,
  socketRef,
  webSocketConnectedRef,
  wsConnectLatchRef,
  persistentDataRequestedRef,
  websocketBaseUrl,
  requestToken,
  handlePersistentData,
  startTranscriptionStreamWithToken,
  startTokenExpiryTimer,
  getSessionIdForConversation,
  setSessionId,
  setDateString,
  setTimeString,
  assemblySocketRef,
  isLive,
  userInitiatedStopRef,
  showPlanWarningToast,
  schedulePlanCountdownFromPayload
}) => {
  const uid = getAuthenticatedUserId();
  logger.info('WebSocket reconnection initiated', {
    component: COMPONENT,
    action: 'RECONNECT_INIT',
    userId: getAuthenticatedUserId(),
    appSessionId: sessionId,
    appConversationId: conversationId || conversationIdFromUrl,
    context: { isLive }
  });
  const effectiveId = conversationId || conversationIdFromUrl;
  
  if (!uid || !sessionId || !effectiveId || effectiveId === 'null') {
    logger.warn('WebSocket reconnection aborted - missing required parameters', {
      component: COMPONENT,
      action: 'RECONNECT_ABORT',
      userId: getAuthenticatedUserId(),
      appSessionId: sessionId,
      appConversationId: effectiveId,
      context: {
        hasUserId: !!uid,
        hasSessionId: !!sessionId,
        hasEffectiveId: !!effectiveId && effectiveId !== 'null'
      }
    });
    return;
  }
  
  if (
    socketRef.current &&
    (socketRef.current.readyState === WebSocket.OPEN ||
     socketRef.current.readyState === WebSocket.CONNECTING)
  ) {
    return;
  }
  
  const state = socketRef.current?.readyState;
  if (webSocketConnectedRef.current && (state === WebSocket.CONNECTING || state === WebSocket.OPEN)) {
    return;
  }
  
  const key = JSON.stringify({ sid: sessionId || null, cid: effectiveId, uid });
  if (wsConnectLatchRef.current.inFlight && wsConnectLatchRef.current.key === key) {
    return;
  }
  
  if (socketRef.current) {
    if (state === WebSocket.OPEN || state === WebSocket.CLOSING) {
      try {
        socketRef.current.onclose = null;
        socketRef.current.onerror = null;
        socketRef.current.onopen = null;
        socketRef.current.onmessage = null;
        socketRef.current.close();
      } catch {}
    } else if (state === WebSocket.CONNECTING) {
      socketRef.current.onopen = null;
      socketRef.current.onclose = null;
      socketRef.current.onerror = null;
      socketRef.current.onmessage = null;
      return;
    }
    socketRef.current = null;
    webSocketConnectedRef.current = false;
  }
  
  wsConnectLatchRef.current = { key, inFlight: true };
  const tokenPayload = {
    user_id: uid,
    session_id: sessionId || effectiveId,
    conversation_id: effectiveId,
    user_type: "speaker",
    connection_source: "web",
  };
  const token = btoa(JSON.stringify(tokenPayload));
  
  if (!token || !websocketBaseUrl) {
    wsConnectLatchRef.current.inFlight = false;
    return;
  }
  
  const wsUrl = `${websocketBaseUrl}?token=${token}`;
  const socket = new window.WebSocket(wsUrl);
  socketRef.current = socket;

  logger.info('Creating WebSocket connection for reconnection', {
    component: COMPONENT,
    action: 'WS_CONNECT',
    userId: getAuthenticatedUserId(),
    appSessionId: sessionId,
    appConversationId: effectiveId,
    context: { wsUrl }
  });
  
  socket.onopen = () => {
    logger.info('WebSocket reconnection successful', {
      component: COMPONENT,
      action: 'WS_OPEN',
      userId: getAuthenticatedUserId(),
      appSessionId: sessionId,
      appConversationId: effectiveId
    });
    console.log('✅ [WebSocket] Connected to server');
    webSocketConnectedRef.current = true;
    socketRef.lastOpenTime = Date.now();
    
    
    if (!persistentDataRequestedRef.current) {
      requestToken(false);
      persistentDataRequestedRef.current = true;
    }
    
    const pingInterval = setInterval(() => {
      if (socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ action: "ping", session_id: sessionId, user_id: uid, conversation_id: effectiveId, user_type: "speaker" }));
      } else {
        clearInterval(pingInterval);
      }
    }, 5000);
    socket.pingInterval = pingInterval;
    
    if (wsConnectLatchRef.current.key === key) {
      wsConnectLatchRef.current.inFlight = false;
    }
  };
  
  socket.onmessage = (message) => {
    // Keep existing message handler logic unchanged
    try {
      if (!message.data) return; // Ignore empty messages
      if (message.data instanceof ArrayBuffer || message.data instanceof Blob) return;
      if (typeof message.data !== 'string') return;
      const data = JSON.parse(message.data);
      const planInfo = normalizeWarningPayload(data);
      handleWarningToastAndCountdown(data, planInfo, showPlanWarningToast, schedulePlanCountdownFromPayload);
      
      if (data.action === "pong") return;if (data.action === "pong") {
        logger.debug('WebSocket pong received', {
          component: COMPONENT,
          action: 'WS_PONG',
          userId: getAuthenticatedUserId(),
          appSessionId: sessionId,
          appConversationId: effectiveId
        });
        return;
      }
      
      if (data.action === "persistent_data" || data.action === "persistentdata" || data.type === "persistentdata") {
        logger.info('Persistent data received', {
          component: COMPONENT,
          action: 'WS_PERSISTENT_DATA',
          userId: getAuthenticatedUserId(),
          appSessionId: sessionId,
          appConversationId: effectiveId
        });
        handlePersistentData(data.data || data);
        return;
      }

      if (data.action === 'configuration_speaker') {
        // Extract model_details from nested structure
        const modelDetails = data.model_details || {};
        
        // Set start time from created_at (persists across refreshes)
        if (data.created_at) {
          // Ensure UTC interpretation by adding 'Z' if no timezone info present
          let createdAtUTC = data.created_at;
          if (!createdAtUTC.endsWith('Z') && !createdAtUTC.includes('+') && !createdAtUTC.includes('-', 10)) {
            createdAtUTC = createdAtUTC + 'Z';
          }
          const createdDate = new Date(createdAtUTC);
          if (!isNaN(createdDate.getTime())) {
            setDateString(createdDate.toLocaleDateString());
            setTimeString(createdDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }).toUpperCase());
          }
        }
        
        if (modelDetails.status === 'new') {
          logger.info('New token received during reconnection', {
            component: COMPONENT,
            action: 'WS_NEW_TOKEN',
            userId: getAuthenticatedUserId(),
            appSessionId: sessionId,
            appConversationId: effectiveId,
            context: { model: modelDetails.model }
          });
          // New token - store in cookies and connect
          storeConfigurationResponse(data);
          
          if (!sessionId && (conversationId || conversationIdFromUrl)) {
            const convId = conversationId || conversationIdFromUrl;
            
            (async () => {
              try {
                const actualSessionId = await getSessionIdForConversation(convId);
                if (actualSessionId) {
                  logger.info('Session ID retrieved for new token', {
                    component: COMPONENT,
                    action: 'SESSION_ID_RETRIEVED',
                    userId: getAuthenticatedUserId(),
                    appSessionId: actualSessionId,
                    appConversationId: convId
                  });
                  // Store the actual session_id for future use
                  setCookie('session_id', actualSessionId);
                  localStorage.setItem('session_id', actualSessionId); // Also store in localStorage for insert_message
                  // Update the sessionId state variable
                  setSessionId(actualSessionId);
                  } else {
                  logger.warn('Failed to retrieve session ID for conversation', {
                    component: COMPONENT,
                    action: 'SESSION_ID_RETRIEVAL_FAILED',
                    userId: getAuthenticatedUserId(),
                    appConversationId: convId
                  });
                }
              } catch (error) {
                logger.error('Failed to fetch session ID during reconnection', {
                  component: COMPONENT,
                  action: 'SESSION_ID_FETCH_ERROR',
                  userId: getAuthenticatedUserId(),
                  appConversationId: convId,
                  error
                });
                console.error('❌ [Token Response] Failed to fetch session_id:', error);
              }
            })();
          }
          
          if (!userInitiatedStopRef.current && !window.isStoppingSession) {
            if (!assemblySocketRef.current || assemblySocketRef.current.readyState !== WebSocket.OPEN) {
              startTranscriptionStreamWithToken(modelDetails.token, modelDetails.model);
              startTokenExpiryTimer();
            } else {
              startTokenExpiryTimer();
            }
          }
        } else if (modelDetails.status === 'existing') {
          // Existing token - update timestamps and check if this is session restoration vs session
          storeConfigurationResponse(data);
          
          if (!sessionId && (conversationId || conversationIdFromUrl)) {
            const convId = conversationId || conversationIdFromUrl;            
            (async () => {
              try {
                const actualSessionId = await getSessionIdForConversation(convId);
                if (actualSessionId) {
                  // Store the actual session_id for future use
                  setCookie('session_id', actualSessionId);
                  localStorage.setItem('session_id', actualSessionId); // Also store in localStorage for insert_message
                  // Update the sessionId state variable
                  setSessionId(actualSessionId);
                  
                  if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
                     if (!persistentDataRequestedRef.current) {
                      const persistentDataPayload = {
                        action: 'persistent_data',
                        user_cognito_id: getAuthenticatedUserId(),
                        session_id: actualSessionId,
                        conversation_id: convId
                      };
                      socketRef.current.send(JSON.stringify(persistentDataPayload));
                      persistentDataRequestedRef.current = true;
                    }
                  }
                }
              } catch (error) {
                logger.error('Failed to fetch session ID for existing token', {
                  component: COMPONENT,
                  action: 'SESSION_ID_FETCH_ERROR',
                  userId: getAuthenticatedUserId(),
                  appConversationId: convId,
                  error
                });
                console.error('❌ [Token Response] Failed to fetch session_id:', error);
              }
            })();
          }
          logger.info('Processing existing token during reconnection', {
            component: COMPONENT,
            action: 'WS_EXISTING_TOKEN',
            userId: getAuthenticatedUserId(),
            appSessionId: sessionId,
            appConversationId: effectiveId
          });
          if (!userInitiatedStopRef.current && !window.isStoppingSession) {
            const validToken = getStoredToken();
            if (validToken) {
              if (!assemblySocketRef.current || assemblySocketRef.current.readyState !== WebSocket.OPEN) {
                startTranscriptionStreamWithToken(validToken);
              }
            } else {
              requestToken(true);
            }
          }
        } else {
          logger.warn('Unknown configuration status received', {
            component: COMPONENT,
            action: 'WS_UNKNOWN_CONFIG_STATUS',
            userId: getAuthenticatedUserId(),
            appSessionId: sessionId,
            appConversationId: effectiveId,
            context: { status: modelDetails.status }
          });
          console.warn('❓ [Configuration] Unknown configuration status:', modelDetails.status);
        }
      }
    } catch (error) {
      // Only log parsing errors for non-empty messages
      if (message.data && message.data.length > 0) {
        logger.error('WebSocket message parsing error', {
          component: COMPONENT,
          action: 'WS_MESSAGE_PARSE_ERROR',
          userId: getAuthenticatedUserId(),
          appSessionId: sessionId,
          appConversationId: effectiveId,
          error,
          context: { dataLength: message.data?.length }
        });
        console.error('❌ [WebSocket] Message parsing error:', error);
      }
    }
  };

  socket.onclose = (event) => {
    const closeDetails = {
      code: event.code,
      reason: event.reason,
      wasClean: event.wasClean
    };


    logger.warn('WebSocket connection closed during reconnection', {
      Event:event,
      component: COMPONENT,
      action: 'WS_CLOSE',
      userId: getAuthenticatedUserId(),
      appSessionId: sessionId,
      appConversationId: effectiveId,
      context: { online: navigator.onLine },
      closeDetails
    });
    console.log('🔌 [WebSocket] Connection closed:', event.code, event.reason);
    webSocketConnectedRef.current = false;
    if (wsConnectLatchRef.current.key === key) {
      wsConnectLatchRef.current.inFlight = false;
    }

    // Check if there's an active session that should trigger reconnection
    const isLiveSessionCookie = getCookie('isLiveSession') === 'true';

    // Only attempt reconnection if this wasn't a normal closure (code 1000)
    // and we have an active live session and user hasn't initiated stop
    // Code 1005: No Status Received, Code 1006: Abnormal Closure
    if ((event.code == 1005 || event.code == 1006) && isLiveSessionCookie && !userInitiatedStopRef.current && !window.isStoppingSession && navigator.onLine) {
      logger.info('Initiating recursive WebSocket reconnection', {
        component: COMPONENT,
        action: 'WS_RECONNECT_RETRY',
        userId: getAuthenticatedUserId(),
        appSessionId: sessionId,
        appConversationId: effectiveId,
        context: { code: event.code, isLiveSession: isLiveSessionCookie }
      });
      reconnectWebSocket({
        getAuthenticatedUserId,
        conversationId,
        conversationIdFromUrl,
        sessionId,
        socketRef,
        webSocketConnectedRef,
        wsConnectLatchRef,
        persistentDataRequestedRef,
        websocketBaseUrl,
        requestToken,
        handlePersistentData,
        startTranscriptionStreamWithToken,
        startTokenExpiryTimer,
        getSessionIdForConversation,
        setSessionId,
        setDateString,
        setTimeString,
        assemblySocketRef,
        isLive,
        userInitiatedStopRef,
        showPlanWarningToast,
        schedulePlanCountdownFromPayload
      });
      console.log("backend disconnected function called");
      } else {
      logger.debug('WebSocket reconnection not triggered', {
        component: COMPONENT,
        action: 'WS_RECONNECT_SKIP',
        userId: getAuthenticatedUserId(),
        appSessionId: sessionId,
        appConversationId: effectiveId,
        context: {
          code: event.code,
          isLiveSession: isLiveSessionCookie,
          userInitiatedStop: userInitiatedStopRef.current,
          isStoppingSession: !!window.isStoppingSession
        }
      });
    }
  };

  socket.onerror = (error) => {
    const errorDetails = {
      message: error.message || 'Unknown WebSocket error',
      type: error.type || 'error',
      readyState: socket.readyState,
      readyStateLabel: ['CONNECTING', 'OPEN', 'CLOSING', 'CLOSED'][socket.readyState] || 'UNKNOWN',
      url: socket.url
    };

    logger.error('WebSocket connection error during reconnection', {
      component: COMPONENT,
      action: 'WS_ERROR',
      userId: getAuthenticatedUserId(),
      appSessionId: sessionId,
      appConversationId: effectiveId,
      context: { online: navigator.onLine },
      error: errorDetails
    });
    console.error('❌ [WebSocket] Connection error:', error);
    webSocketConnectedRef.current = false;
    if (wsConnectLatchRef.current.key === key) {
      wsConnectLatchRef.current.inFlight = false;
    }
    
  };
};